/**
 * useMailSend — encapsulates the manual inline reply send pipeline.
 *
 * Responsibilities:
 *   1. resolve the sender account (live footer state via resolveFooterForSend)
 *   2. build the sendWorldEmail payload (utils/replyPayload)
 *   3. hand the actual HTTP call to the global InboxSendJobs registry so it
 *      keeps running (and stays visible in the corner sending-bar) even if
 *      the user navigates away from the inbox mid-send
 *   4. mark the original email as replied on success
 *
 * The backend endpoint is unchanged — server behaviour (queues, SafeGuard,
 * tracking) is identical to the classic inbox flow.
 */

import { useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../../../utils/cloudFunctions';
import { resolveFooterForSend } from '../../EmailAiModes/resolveFooterForSend';
import { buildReplyPayload, buildForwardPayload } from '../utils/replyPayload';
import { useInboxSendJobs } from '../../../contexts/InboxSendJobsContext';

/**
 * Rough expected end-to-end duration of a manual reply send. Used only to
 * animate the corner progress bar smoothly toward ~95% until the real
 * response comes back. Actual send times vary from ~4s to ~16s depending on
 * SMTP/Graph, footer image processing and attachments.
 */
const EXPECTED_SEND_DURATION_MS = 12000;

/**
 * @param {object} args
 * @param {string} args.userId
 * @param {object} args.userSettings footer fallback settings (RTDB settings tree)
 */
export default function useMailSend({ userId, userSettings = {} }) {
  const functions = getFunctions(undefined, 'europe-west1');
  const { startJob } = useInboxSendJobs();

  /**
   * Runs the actual send inside the global job registry. Returns the job id
   * synchronously (or `null` if the request was rejected before enqueue).
   *
   * The caller is expected to close its composer / apply optimistic UI in the
   * same tick — the returned promise for HTTP completion is fired via the
   * onSuccess/onError callbacks, not awaited here.
   */
  const sendReply = useCallback(({ account, originalEmail, leadId = null, isNewEmail = false, text, subject, recipients, attachments = [], cc = [], bcc = [], onSuccess, onError }) => {
    if (!account) {
      const res = { success: false, message: 'No sender account resolved.' };
      onError?.(res);
      return null;
    }
    const clean = (recipients || []).filter(Boolean);
    if (clean.length === 0) {
      const res = { success: false, message: 'No recipient address.' };
      onError?.(res);
      return null;
    }

    return startJob({
      kind: 'reply',
      recipient: clean[0],
      subject,
      senderEmail: account?.email || '',
      expectedDurationMs: EXPECTED_SEND_DURATION_MS,
      onSuccess,
      onError,
      execute: async () => {
        const { footerId, footerSettings } = await resolveFooterForSend({
          userId,
          senderAccountId: account.id,
          fallbackFooterId: account.footerId || null,
          fallbackFooterSettings: userSettings,
        });

        const payload = buildReplyPayload({
          userId,
          account,
          plainText: text,
          subject,
          recipients: clean,
          originalEmail,
          footerSettings,
          footerId,
          attachments,
          cc,
          bcc,
          leadId,
          isNewEmail,
        });

        const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.sendWorldEmail(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const raw = await response.text();
        let data;
        try {
          data = JSON.parse(raw);
        } catch (e) {
          return { success: false, message: `Invalid server response: ${raw.slice(0, 120)}` };
        }

        if (data.success) {
          // SafeGuard / pre-send scorer can intercept and save as draft instead.
          if (data.drafted) {
            return {
              success: true,
              drafted: true,
              message: data.safeGuardRating !== undefined
                ? `Saved as draft (quality ${data.safeGuardRating}/${data.minimumRating || 10}).`
                : 'Saved as draft for manual review.',
            };
          }

          // Best-effort: mark the original inbox email as replied. Failures
          // here are non-fatal — the mail has already gone out.
          if (originalEmail?.id) {
            try {
              const markReplied = httpsCallable(functions, 'markEmailAsReplied');
              await markReplied({ emailId: originalEmail.id, userId });
            } catch (markErr) {
              console.warn('[useMailSend] markEmailAsReplied failed (non-fatal):', markErr?.message);
            }
          }

          return { success: true, message: isNewEmail ? 'Email sent.' : 'Reply sent.' };
        }

        return { success: false, message: data.message || data.error || 'Send failed.' };
      },
    });
  }, [userId, userSettings, functions, startJob]);

  /**
   * Forwards a message to an arbitrary address. Same backend endpoint, but a
   * forward-shaped payload (isForward: true, leadId: null). Does NOT mark the
   * original as replied.
   */
  const sendForward = useCallback(({ account, originalEmail, forwardMessages, text, subject, recipients, attachments = [], cc = [], bcc = [], onSuccess, onError }) => {
    if (!account) {
      const res = { success: false, message: 'No sender account resolved.' };
      onError?.(res);
      return null;
    }
    const clean = (recipients || []).filter(Boolean);
    if (clean.length === 0) {
      const res = { success: false, message: 'No recipient address.' };
      onError?.(res);
      return null;
    }

    return startJob({
      kind: 'forward',
      recipient: clean[0],
      subject,
      senderEmail: account?.email || '',
      expectedDurationMs: EXPECTED_SEND_DURATION_MS,
      onSuccess,
      onError,
      execute: async () => {
        const { footerId, footerSettings } = await resolveFooterForSend({
          userId,
          senderAccountId: account.id,
          fallbackFooterId: account.footerId || null,
          fallbackFooterSettings: userSettings,
        });

        const payload = buildForwardPayload({
          userId,
          account,
          plainText: text,
          subject,
          recipients: clean,
          originalEmail,
          forwardMessages,
          footerSettings,
          footerId,
          attachments,
          cc,
          bcc,
        });

        const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.sendWorldEmail(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const raw = await response.text();
        let data;
        try {
          data = JSON.parse(raw);
        } catch (e) {
          return { success: false, message: `Invalid server response: ${raw.slice(0, 120)}` };
        }

        if (data.success) {
          if (data.drafted) {
            return {
              success: true,
              drafted: true,
              message: data.safeGuardRating !== undefined
                ? `Saved as draft (quality ${data.safeGuardRating}/${data.minimumRating || 10}).`
                : 'Saved as draft for manual review.',
            };
          }
          return { success: true, message: 'Forwarded.' };
        }

        // The server refuses to send to opted-out / blacklisted addresses.
        if (data.optOutBlocked) {
          return { success: false, message: data.error || 'Recipient is blocked.' };
        }

        return { success: false, message: data.message || data.error || 'Forward failed.' };
      },
    });
  }, [userId, userSettings, startJob]);

  // `sending` is retained (always false) so existing consumers that still
  // destructure it don't break. The global sending-bar is now the source of
  // truth for in-flight state.
  return { sending: false, sendReply, sendForward };
}
